import { apis } from '@src/shared/api';
import { logger } from '@src/shared/includes/logger';
import { useQueries } from '@tanstack/react-query';
import { useToast } from '@uhg-abyss/web/hooks/useToast';
import { isFunction, isObject, isString, isUndefined, merge } from 'lodash';

/**
 * Hook: useApiQueries
 *
 * Make multiple api requests in parallel.
 *
 * @param apiKey
 * @param queries
 * @returns {{}}
 */
export function useApiQueries(apiKey = 'Default', queries = []) {
  const { toast } = useToast();

  const theQueries = useQueries({
    queries: queries.map((query) => {
      const queryKey = [];

      if (isString(query)) {
        queryKey.push(apiKey);
        queryKey.push(query);
      }

      if (isObject(query)) {
        queryKey.push(query?.apiKey || apiKey);
        queryKey.push(query?.queryKey);
        queryKey.push(query?.payload);
      }

      const theApiKey = queryKey?.[0] || apiKey;
      let theQueryKey = queryKey?.[1];
      if (theQueryKey.includes('-')) {
        const keyItems = theQueryKey.split('-');
        theQueryKey = keyItems?.[0];
      }

      let queryFunction;

      if (!isUndefined(apis?.[theApiKey]?.queries?.[theQueryKey])) {
        queryFunction = apis?.[theApiKey]?.queries?.[theQueryKey];
      }

      return merge(
        {},
        {
          excludedHttpCodes: [],
          queryFn: async (args) => {
            try {
              let response;

              if (isUndefined(queryFunction)) {
                throw new Error(
                  `Request not found for api key: ${args?.queryKey?.[0]} and query key: ${args?.queryKey?.[1]}`
                );
              }

              if (isFunction(queryFunction)) {
                response = await queryFunction(args?.queryKey);
              }

              return {
                [queryKey[1]]: response,
              };
            } catch (errorObject) {
              const error = errorObject?.error || errorObject;
              const excludedHttpCodes = query?.options?.excludedHttpCodes || [];

              if (!excludedHttpCodes?.includes(error?.statusCode)) {
                toast.show({
                  autoClose: false,
                  id: `useApiQueries-${theApiKey}-${theQueryKey}`,
                  message: error?.message || 'Unknown error',
                  title: `API Error - ${theQueryKey}`,
                  variant: 'error',
                });
              }

              logger.error('src/app/context/Api/hooks/useApiQueries/useApiQueries.jsx -> useApiQueries', false, error);
              throw errorObject;
            }
          },
          queryKey,
        },
        query?.options || {}
      );
    }),
  });

  const response = {};

  theQueries.forEach((theQuery) => {
    const query = { ...theQuery };
    const keys = !isUndefined(theQuery?.data) ? Object.keys(theQuery?.data) : [];
    let key = keys[0];

    if (isUndefined(key) && theQuery?.error?.error?.request?.parameters?.queryKey) {
      key = theQuery.error.error.request.parameters.queryKey;
    }

    query.data = !isUndefined(theQuery?.data) ? theQuery?.data?.[key] : undefined;
    response[key] = query;
    return true;
  });

  return response;
}
